import { useId } from 'react';
import { Eye, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle
} from '@/components/ui/drawer';
import { Modal } from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { Typography } from '@/components/ui/typography';
import { DESKTOP_BREAKPOINT_QUERY } from '@/config/ui.constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';
import NotifyTeamBlock from './NotifyTeamBlock';
import { NOTIFY_COPY } from './notifyCopy';
import { VisibilityHeading } from './PhotosetVisibilityDialog';
import type { NotifyAfterReveal } from './usePhotosetVisibility';
import { useNotifyTeam } from './useNotifyTeam';
import { dialogCopy } from './visibilityCopy';

interface MakeVisibleNotifyDialogProps {
  photosetId: string;
  accountName: string | null;
  saving: boolean;
  error: string | null;
  /** `notify` is passed when the email is on. */
  onConfirm: (notify?: NotifyAfterReveal) => void;
  onCancel: () => void;
}

/**
 * DV-3563: the Make Visible dialog with the Send Email Notification block,
 * shown in place of DV-2861's while the `photosetShare` release flag is on.
 * The Hide dialog is unchanged. A dialog at lg+ and, per the AC, a
 * full-width bottom sheet below it with Cancel and the confirm button side
 * by side.
 *
 * While the reveal or its email is in flight nothing closes it: the close X
 * is disabled, and the sheet cannot be swiped away (vaul's own
 * `dismissible`: a swipe it is not allowed to finish would leave the sheet
 * stuck part-way off-screen). Escape and a click outside still reach
 * `onCancel`, which the hook refuses.
 */
const MakeVisibleNotifyDialog = ({
  photosetId,
  accountName,
  saving,
  error,
  onConfirm,
  onCancel
}: MakeVisibleNotifyDialogProps) => {
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT_QUERY);
  const notify = useNotifyTeam(photosetId);
  const lineId = useId();
  const copy = dialogCopy('visible', accountName);
  const heading = <VisibilityHeading target="visible" title={copy.title} />;

  const body = (
    <div className="flex flex-col gap-4">
      <Typography as="p" variant="body" className="text-muted-foreground">
        {copy.body}
      </Typography>
      <NotifyTeamBlock notify={notify} disabled={saving} lineId={lineId} />
      {error && (
        <Typography
          as="p"
          variant="body"
          role="alert"
          className="text-destructive"
        >
          {error}
        </Typography>
      )}
    </div>
  );

  const cancelButton = (
    <Button
      type="button"
      variant="outline"
      onClick={onCancel}
      disabled={saving}
      autoFocus
    >
      Cancel
    </Button>
  );
  const confirmButton = (
    <Button
      type="button"
      onClick={() => onConfirm(notify.emailOn ? notify.send : undefined)}
      disabled={saving || !notify.ready}
      aria-describedby={notify.block !== null ? lineId : undefined}
    >
      {saving ? (
        <Spinner size="sm" className="text-current" />
      ) : (
        <Eye className="h-4 w-4 max-sm:hidden" />
      )}
      {notify.emailOn ? NOTIFY_COPY.confirmNotify : copy.confirmLabel}
    </Button>
  );

  if (!isDesktop) {
    return (
      <Drawer
        open
        onOpenChange={(next) => {
          if (!next) onCancel();
        }}
        dismissible={!saving}
      >
        <DrawerContent
          data-testid="make-visible-sheet"
          className="max-h-[92dvh]"
          aria-describedby={undefined}
        >
          <div className="flex shrink-0 items-center gap-2 border-b border-border py-2 pl-4 pr-2">
            <DrawerTitle asChild>
              <Typography as="h2" variant="heading-md" className="min-w-0 flex-1">
                {heading}
              </Typography>
            </DrawerTitle>
            <DrawerClose
              aria-label="Close"
              disabled={saving}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </DrawerClose>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">{body}</div>
          {/* Side by side at equal width (AC); the bottom inset keeps them
              clear of the iOS home indicator. */}
          <div
            className={cn(
              'grid shrink-0 grid-cols-2 gap-2 border-t border-border px-4 pt-3',
              'pb-[max(0.75rem,env(safe-area-inset-bottom))]'
            )}
          >
            {cancelButton}
            {confirmButton}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Modal
      open
      // Escape, the backdrop and the corner X all arrive here; the hook
      // refuses them while a save is in flight.
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      size="md"
      title={heading}
      closeDisabled={saving}
      footer={
        <>
          {cancelButton}
          {confirmButton}
        </>
      }
    >
      {body}
    </Modal>
  );
};

export default MakeVisibleNotifyDialog;
